import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import AspectRatio from 'react-aspect-ratio';
import {useDispatch, useSelector} from "react-redux";
import {has, isNil} from 'lodash';
import hstatic, {staticHypotheekbondUrl} from "@dnhb/content-api-client/src/util/hstatic";
import {LAYOUT_CONSTANTS, SKELETON_LOADER} from "../../const";
import useCurrentRoute from "../../hooks/useCurrentRoute";
import useImageLoaded from "../../hooks/useImageLoaded";
import {RootState} from "../../redux/store";
import {ArticleContext} from './ArticleCard';
import {setArticlesFailed, setArticlesReady} from "../../redux/applicationActions";
import {INITIAL_ROUTE} from "../../hooks/useRoutes";
import {ContentContainer} from "../Containers";
import {Caption, Figure} from "../Content";
import useMedia from "../../hooks/useMedia";
import {ReactSVG} from "react-svg";

const {CONTENT_BASE_WIDTH, COLUMN_COUNT, FORM_ROW_PADDING, GUTTER_SIZE, CONTENT_MAX_WIDTH} = LAYOUT_CONSTANTS;
const {SKELETON_LOADER_BG, SKELETON_LOADER_FG} = SKELETON_LOADER;

interface Props {
  columns: number;
  maxWidth: number;
  height: number;
  ratio: number;
  compact?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: {[key: string]: number|string}
}

const ArticleFeaturedImage: React.FC<Props> = ({
  compact = true,
  columns = 1,
  maxWidth,
  height,
  ratio = 1,
  children,
  className,
  style,
}): React.ReactElement => {
  const pathname = useCurrentRoute();
  const {onMobile, onTablet} = useMedia();
  const dispatch = useDispatch();
  const {imgProps, isLoading, loadingFailed, loadingSuccessful} = useImageLoaded();
  const {articles_failed, articles_ready, articles_visible} = useSelector((state: RootState) => {
    const {routes} = state.application;
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles_failed, articles_ready, articles_visible} = route;

    return {
      articles_failed: (articles_failed || []),
      articles_ready: (articles_ready || []),
      articles_visible: (articles_visible || []),
    }
  });
  const [width, setWidth] = useState<number>(null);
  if (!maxWidth) maxWidth = (COLUMN_COUNT * CONTENT_BASE_WIDTH) + (COLUMN_COUNT - 1) * GUTTER_SIZE; // restore max-width on mobile
  let image, uuid, visible;

  useEffect(() => {
    if (maxWidth && columns) {
      setWidth(Math.floor(maxWidth / columns));
    } else {
      setWidth(Math.floor(height * ratio));
    }
  }, [columns, ratio, maxWidth, height]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isNil(uuid)) return;
    if (!uuid) dispatch(setArticlesFailed(pathname, [...articles_failed, uuid]));

    if (!isLoading) {
      if (loadingSuccessful) {
        dispatch(setArticlesReady(pathname, [...articles_ready, uuid]));
      } else if (loadingFailed) {
        dispatch(setArticlesFailed(pathname, [...articles_failed, uuid]));
      }
    }
  }, [dispatch, isLoading, uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const getImageCrop = useCallback((url, size) => {
    const imageSize = Math.floor(width*size);
    const settings = (width < 240)
      ? `?w=${Math.floor(imageSize*2.4)}&cx=${Math.floor(imageSize*0.2)}&cw=${imageSize*2}` // slight zoom
      : `?w=${imageSize}`;

    return `${url}${settings}`;
  }, [width]);

  return width && (
    <ArticleContext.Consumer>
      {({content: {featuredImage, id, imageCredit, title, visible: isVisible}}) => {
        uuid = featuredImage ? id : false;
        image = (featuredImage && (isLoading || loadingSuccessful))
          ? <img
              {...imgProps}
              alt={title}
              src={getImageCrop(featuredImage, 1)}
              srcSet={`${getImageCrop(featuredImage, 1)} 1x, ${getImageCrop(featuredImage, 2)} 2x, ${getImageCrop(featuredImage, 3)} 3x`}
              width={width}
            />
          : null;
        visible = isVisible || (articles_visible || []).find(id => id === String(uuid));

        return (
          <AspectRatio
            ratio={ratio}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              minHeight: `${Math.ceil(height)}px`,
              width: 'auto',
              maxWidth: onMobile ? '100%' : 'unset',
              position: 'relative',
              background: isLoading || !visible ? SKELETON_LOADER_BG : 'unset',
              fill: isLoading || !visible ? SKELETON_LOADER_FG : 'unset',
              ...style
            }}
            className={className}
          >
            <>
              <Figure
                className={`${isLoading || !visible ? 'loading': ''}${String(className).includes('spotlight') ? ' spotlight' : ''} ${className}`}
                style={{
                  overflow: 'hidden',
                  width: '100%',
                  minHeight: 'inherit',
                  position: 'relative'
                }}
              >
                {image}

                <ContentContainer style={{position: 'absolute', left: 0, bottom: 0, zIndex: 1, padding: FORM_ROW_PADDING}}>
                  {children}
                </ContentContainer>
              </Figure>

              {!compact && !(isLoading || !visible) && (
                <Caption
                  className={"theme--secondary-color f-7 fw-600"}
                  style={{
                    bottom: `calc(${FORM_ROW_PADDING} * -1.5)`,
                    left: onMobile ? 0 : onTablet ? 0 : 'auto',
                    ...((onTablet && !onMobile) ? {
                      left: 'auto',
                      maxWidth: `${CONTENT_MAX_WIDTH}px`,
                      width: '100%',
                      right: 'auto',
                      alignSelf: 'center',
                      justifyContent: 'flex-end',
                    } : {})
                  }}
                >
                  <ReactSVG
                    src={hstatic(`${staticHypotheekbondUrl()}images/icon/img.svg`)}
                    style={{padding: '0 5px 0 2px', marginTop: '1px', width: '12px'}}
                  />
                  {imageCredit}
                </Caption>
              )}
            </>
          </AspectRatio>
        )
      }}
    </ArticleContext.Consumer>
  )
};

export default ArticleFeaturedImage;
